import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import i18next from 'i18next'

import { getLocations, getLocationsCount } from '../utils/api'
import isNetworkError from '../utils/isNetworkError'
import { addLocationToList, removeLocationFromList } from './saveSlice'
import { selectParams } from './selectParams'
import { updateSelection } from './updateSelection'
import { updateLastMapView } from './viewportSlice'

export const fetchListLocations = createAsyncThunk(
  'list/fetchListLocations',
  async ({ offset, fetchCount }, { getState }) => {
    const state = getState()
    const { types, muni } = state.filter
    const { lastMapView } = state.viewport
    const { bounds, zoom, center } = lastMapView
    const params = selectParams(
      { types, muni, bounds, zoom, center },
      { limit: 100, offset, photo: true },
    )
    return {
      offset,
      locations: await getLocations(params),
      ...(fetchCount && (await getLocationsCount(params))),
    }
  },
)

export const fetchListLocationsStart = () =>
  fetchListLocations({ fetchCount: true, offset: 0 })
export const fetchListLocationsExtend = (locations) =>
  fetchListLocations({ fetchCount: false, offset: locations.length })

export const listSlice = createSlice({
  name: 'list',
  initialState: {
    isLoading: false,
    totalCount: null,
    shouldFetchNewLocations: true,
    fetchError: null,
    locations: [],
    lastMapView: null,
    lastViewedListPositionId: null,
    lastViewedOffsetTop: null,
    lastViewedScrollTop: null,
  },
  reducers: {
    setLastViewedListPositionState: (state, action) => {
      const { id, offsetTop, scrollTop } = action.payload

      state.lastViewedListPositionId = id
      state.lastViewedOffsetTop = offsetTop
      state.lastViewedScrollTop = scrollTop
    },
    clearLastViewedListPositionState: (state) => {
      state.lastViewedListPositionId = null
      state.lastViewedOffsetTop = null
      state.lastViewedScrollTop = null
    },
    markShouldFetchNewLocations: (state) => {
      state.shouldFetchNewLocations = true
    },
  },
  extraReducers: {
    [fetchListLocations.pending]: (state) => {
      state.shouldFetchNewLocations = false
      state.isLoading = true
    },
    [fetchListLocations.fulfilled]: (state, action) => {
      const { offset, locations, count } = action.payload

      if (offset > 0) {
        state.locations.push(...locations)
      } else {
        state.locations = locations
      }

      if (count !== undefined) {
        state.totalCount = count
      }

      state.isLoading = false
      state.shouldFetchNewLocations = false
      state.fetchError = null
    },
    [fetchListLocations.rejected]: (state, action) => {
      state.isLoading = false
      state.fetchError = i18next.t('error_message.api.fetch_locations_failed', {
        message: isNetworkError(action.error)
          ? i18next.t('error_message.connectivity.you_are_offline')
          : action.error.message || i18next.t('error_message.unknown_error'),
      })
    },
    [updateSelection.type]: (state) => {
      state.shouldFetchNewLocations = true
    },
    [updateLastMapView]: (state) => {
      state.shouldFetchNewLocations = true
    },
    [addLocationToList.fulfilled]: (state, action) => {
      const { location } = action.payload
      const index = state.locations.findIndex((loc) => loc.id === location.id)
      if (index !== -1) {
        state.locations[index].in_list = true
      }
    },
    [removeLocationFromList.fulfilled]: (state, action) => {
      const { locationId, locationStillInAnyList } = action.payload
      const index = state.locations.findIndex((loc) => loc.id === locationId)
      if (index !== -1) {
        state.locations[index].in_list = locationStillInAnyList
      }
    },
  },
})

export const {
  setLastViewedListPositionState,
  clearLastViewedListPositionState,
  markShouldFetchNewLocations,
} = listSlice.actions

export default listSlice.reducer
